import {
  type HeadCell,
  type DropDownSelectOption,
  booleanStringYNOptions,
  type FilterType,
  type Filter,
  getArrayTableCell,
  type SelectOption,
} from '../../../../components';
import { universityCategoryOptions } from '../../../Application/components/ApplicationForm/ProgrammeChoice';
import { identityTypeFullOptions } from '../../../Signup/components';
import { currentStatus, MONITOR_LIST_TEXT } from '../../../Assessment';
import {
  FUNDING_STATUS_DISPLAY_STRING,
  FUNDING_STATUS,
} from '../../../ScholarshipManagement/constants/fundingStatus';
import { FundingSource } from '../../../Nomination/components/NominationForm/FundingModule/types';
import { type ApplicationViewTableDataItem } from './graphql';
import {
  type ApplicationViewTableFilterFields,
  type QuickFilter,
} from './types';
import { programmeFilterOptions, programmeTypeFilterOptions } from './options';
import { PROGRAMME_TYPE_FILTER } from './constants';
import { assignee } from '../../../Assessment/GSAssessment/components/GSToDoList/assignee';
import { gsSubmissionTimeCell } from '../../../Assessment/GSAssessment/components/GSToDoList/gsSubmissionTimeCell';

const hkpfsApplicationFilter = {
  hkpfsInformation: {
    HKPFSReferenceNumber: { nin: ['', null] },
  },
};
const pppfsApplicationFilter = {
  distinctiveScholarships: {
    fundingSource: {
      in: [FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME],
    },
  },
};
const ipfsApplicationFilter = {
  distinctiveScholarships: {
    fundingSource: {
      in: [FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME],
    },
  },
};

const applicantApplyFundingFilterMap = {
  [FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME]: hkpfsApplicationFilter,
  [FundingSource.POLYU_PRESIDENTIAL_PHD_FELLOWSHIP_SCHEME]:
    pppfsApplicationFilter,
  [FundingSource.INTERNATIONAL_PHD_FELLOWSHIP_SCHEME]: ipfsApplicationFilter,
};

const getFundingStatusFilterOptions = (
  fundingStatusFilter: typeof FUNDING_STATUS,
  fundingStatusDisplayString: typeof FUNDING_STATUS_DISPLAY_STRING,
): DropDownSelectOption[] => {
  return Object.entries(fundingStatusFilter).map(([key, value]) => {
    return {
      value,
      label: fundingStatusDisplayString[
        key as keyof typeof fundingStatusDisplayString
      ] as string,
    };
  });
};

const commonCustomCellStyle = {
  textAlign: 'left' as 'left',
  padding: '1rem',
  flex: 0.2,
};
export const getTableHeader = (
  gsAssigneeOption: SelectOption[],
): Array<HeadCell<ApplicationViewTableDataItem>> => {
  const overViewCells: Array<HeadCell<ApplicationViewTableDataItem>> = [
    {
      id: 'applicationNo',
      label: 'Application No.',
      customStyle: {
        minWidth: '100px',
        width: '100px',
        ...commonCustomCellStyle,
      },
      isSortable: false,
      initialVisible: true,
      render: row => row?.applicationNo,
    },
    {
      id: 'studentNumber',
      label: 'Student No.',
      isSortable: false,
      render: row => row?.studentNumber,
    },
    {
      id: 'applicantName',
      label: 'Applicant Name',
      customStyle: { width: '120px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => getArrayTableCell(row?.applicantName),
    },
    {
      id: 'applicantEmail',
      label: 'Applicant Email',
      isSortable: false,
      render: row => row?.applicantEmail,
    },
    {
      id: 'applicantNationality',
      label: 'Applicant Nationality',
      customStyle: { width: '100px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => row?.applicantNationality,
    },
    {
      id: 'applicantIdentityInfo',
      label: 'Applicant Identity',
      customStyle: { width: '280px', ...commonCustomCellStyle, flex: 1 },
      isSortable: false,
      render: row => row?.applicantIdentityInfo,
    },
    {
      id: 'entryYear',
      label: 'Entry Year',
      customStyle: { width: '80px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => row?.entryYear,
    },
    {
      id: 'department',
      label: 'Dept.',
      customStyle: { width: '80px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => row?.department,
    },
    {
      id: 'programmeCode',
      label: 'Prog Code',
      customStyle: { width: '80px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => row?.programmeCode,
    },
    {
      id: 'applyProgramme',
      label: 'Prog.',
      customStyle: { width: '135px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => getArrayTableCell(row?.applyProgramme),
    },
    {
      id: 'researchAreas',
      label: 'Research Areas',
      customStyle: { width: '150px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => {
        return getArrayTableCell(row.researchAreas);
      },
    },
    {
      id: 'proposedChiefSupervisors',
      label: 'Proposed Chief Supervisors',
      customStyle: {
        width: '100px',
        minWidth: '100px',
        ...commonCustomCellStyle,
      },
      isSortable: false,
      render: row => {
        return getArrayTableCell(row.proposedChiefSupervisors);
      },
    },
    {
      id: 'confirmedChiefSupervisor',
      label: 'Confirmed Chief Supervisor',
      customStyle: {
        width: '100px',
        minWidth: '100px',
        ...commonCustomCellStyle,
      },
      isSortable: false,
      render: row => getArrayTableCell(row.confirmedChiefSupervisor),
    },
    {
      id: 'supervisoryTeam',
      label: 'Supervisory Team',
      customStyle: { width: '150px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => {
        return getArrayTableCell(row.supervisoryTeam);
      },
    },
    {
      id: 'partnerUniversityCategory',
      label: 'PartnerU Category',
      customStyle: commonCustomCellStyle,
      isSortable: false,
      render: row => row.partnerUniversityCategory,
    },
    {
      id: 'partnerUniversity',
      label: 'PartnerU',
      customStyle: { minWidth: '150px', ...commonCustomCellStyle, flex: 1 },
      isSortable: false,
      render: row => row.partnerUniversity,
    },
    {
      id: 'partnerIsPolyUHomeUniversityIndicator',
      label: 'PolyU Home Ind.',
      isSortable: false,
      render: row => row.partnerIsPolyUHomeUniversityIndicator,
    },
    {
      id: 'partnerProposedChiefSupervisor',
      label: 'PartnerU Proposed Chief Supervisor.',
      isSortable: false,
      render: row => row.partnerProposedChiefSupervisor,
    },
    {
      id: 'partnerUniversityApplicationNo',
      label: 'PartnerU Application No.',
      isSortable: false,
      render: row => row.partnerUniversityApplicationNo,
    },
    {
      id: 'scholarshipApplications',
      label: 'Scholarship Application',
      isSortable: false,
      customStyle: commonCustomCellStyle,
      initialVisible: true,
      render: row => {
        return getArrayTableCell(row.scholarshipApplications);
      },
    },
    {
      id: 'hkpfsDisplayInformation',
      label: 'HKPFS Ref. No. | Valid',
      customStyle: { width: '116px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => getArrayTableCell(row.hkpfsDisplayInformation),
    },
    {
      id: 'nominatedFundings',
      label: 'Nominated Funding | Funding Status',
      isSortable: false,
      initialVisible: true,
      customStyle: { minWidth: '150px', ...commonCustomCellStyle, flex: 1 },
      render: row => {
        return getArrayTableCell(row.nominatedFundings);
      },
    },
    {
      id: 'meetConditionCheckUpdatedAt',
      label: 'Meet Condition Check Updated At',
      customStyle: { minWidth: '100px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => row.meetConditionCheckUpdatedAt,
    },
    {
      id: 'meetConditionCheckDeadline',
      label: 'Meet Condition Check Deadline',
      customStyle: { minWidth: '100px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => row.meetConditionCheckDeadline,
    },
    {
      id: 'applicationSubmissionTime',
      label: 'Submission Time (Applicant)',
      customStyle: { minWidth: '100px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => row.applicationSubmissionTime,
    },
    {
      id: 'nominationSubmissionTime',
      label: 'Submission Time (CS to DoRPgS)',
      customStyle: { minWidth: '100px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      render: row => row.nominationSubmissionTime,
    },
    {
      id: 'gsSubmissionTime',
      label: 'Submit to GS Date',
      customStyle: { minWidth: '100px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: false,
      render: row => gsSubmissionTimeCell(row),
    },
    {
      id: 'offerDate',
      label: 'Offer Date',
      customStyle: { minWidth: '140px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: false,
      render: row => row.offerDate,
    },
    {
      id: 'offerStatus',
      label: 'Offer Status',
      customStyle: { width: '116px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: false,
      getHoverText: row => {
        return row.offerStatusExplanation;
      },
      render: row => {
        return row.offerStatus;
      },
    },
    {
      id: 'applicationStatus',
      label: 'Application Status',
      customStyle: { width: '116px', ...commonCustomCellStyle },
      isSortable: false,
      initialVisible: true,
      getHoverText: row => {
        return row.applicationStatusExplanation;
      },
      render: row => {
        return row.applicationStatus;
      },
    },
    {
      id: 'registrationFeePaymentDeadline',
      label: 'Registration Fee Payment Deadline',
      customStyle: { ...commonCustomCellStyle },
      isSortable: false,
      render: row => row.registrationFeePaymentDeadline,
    },
    {
      id: 'meetConditionCheckStatus',
      label: 'Meet Condition Status',
      isSortable: false,
      render: row => row.meetConditionCheckStatus,
    },
    {
      id: 'qualificationCheckStatus',
      label: 'Qualification Check Status',
      isSortable: false,
      render: row => row.qualificationCheckStatus,
    },
    {
      id: 'visaApplicationStatus',
      label: 'Visa Application Status',
      isSortable: false,
      render: row => row.visaApplicationStatus,
    },
    {
      id: 'commencementDate',
      label: 'Commencement Date',
      customStyle: { width: '116px', ...commonCustomCellStyle },
      isSortable: false,
      render: row => row.commencementDate,
    },
    {
      id: 'gsAssignee',
      label: 'Gs Assignee',
      isSortable: false,
      initialVisible: false,
      render: (row, refetch) => assignee(row, gsAssigneeOption, refetch),
    },
  ];

  return overViewCells;
};

export const getTableFilter = (
  onlyPaidApplicationStatusList: boolean = true,
  entryYears: DropDownSelectOption[] = [],
  departments: DropDownSelectOption[] = [],
  fundingSources: DropDownSelectOption[] = [],
  applicantApplyScholarships: DropDownSelectOption[] = [],
  prgCollabPhdPartnerList: DropDownSelectOption[] = [],
  rpgJoinPhdPartnerList: DropDownSelectOption[] = [],
  gsAssigneeOption: DropDownSelectOption[] = [],
): ApplicationViewTableFilterFields => {
  const filters = {
    entryYear: {
      title: 'Entry Year',
      type: 'select' as FilterType,
      options: entryYears,
      displayLabel: false,
      filterKey: 'programmeChoice.entryYear',
    },
    department: {
      title: 'Department',
      type: 'select' as FilterType,
      options: departments,
      displayLabel: false,
      filterKey: 'programmeChoice.department',
    },
    academicProgramme: {
      title: 'Academic Programme',
      type: 'select' as FilterType,
      options: programmeFilterOptions,
      filterKey: 'programmeChoice.programme',
    },
    programmeType: {
      title: 'Programme Type',
      type: 'select' as FilterType,
      options: programmeTypeFilterOptions,
      filterKey: 'programmeChoice.programmeType',
    },

    dualPartnerUniversity: {
      title: 'Dual Partner University',
      type: 'select' as FilterType,
      options: rpgJoinPhdPartnerList,
      convertFilterFunction: (value: string | string[]) => {
        return {
          programmeChoice: {
            partner: { university: { in: value } },
            programmeType: {
              eq: PROGRAMME_TYPE_FILTER.DUAL_PHD_DEGREE_PROGRAMME,
            },
          },
        };
      },
    },

    jointpartnerUniversity: {
      title: 'Joint Partner Institution',
      type: 'select' as FilterType,
      options: prgCollabPhdPartnerList,
      convertFilterFunction: (value: string | string[]) => {
        return {
          programmeChoice: {
            partner: { university: { in: value } },
            programmeType: {
              eq: PROGRAMME_TYPE_FILTER.COLLABORATIVE_PHD_DEGREE_PROGRAMME,
            },
          },
        };
      },
    },

    partnerCategory: {
      title: 'Partner University/Institution Category',
      type: 'select' as FilterType,
      options: universityCategoryOptions as DropDownSelectOption[],
      filterKey: 'programmeChoice.partner.universityCategory',
    },

    applicantIdentityType: {
      title: 'Applicant Identity Type',
      type: 'select' as FilterType,
      options: identityTypeFullOptions as DropDownSelectOption[],
      filterKey: 'applicant.identity.identityType',
    },
    applicantLocalIndicator: {
      title: 'Applicant Local Indicator',
      type: 'singleSelect' as FilterType,
      options: booleanStringYNOptions,
      filterKey: 'applicant.identity.isLocal',
      convertFilterFunction: (value: string | string[]) => {
        return {
          applicant: { identity: { isLocal: value === 'true' } },
        };
      },
    },
    scholarshipAppliedByApplicant: {
      title: 'Scholarship(s) Applied by Applicant',
      fullWidth: true,
      type: 'select' as FilterType,
      options: applicantApplyScholarships,
      convertFilterFunction: (value: string | string[]) => {
        const arrayValue = value as string[];
        const returnedValue = arrayValue.map(ele => {
          return applicantApplyFundingFilterMap[
            ele as keyof typeof applicantApplyFundingFilterMap
          ];
        });
        return { or: returnedValue };
      },
    },
    hkpfsNo: {
      title: 'HKPFS Reference No.',
      type: 'text' as FilterType,
      filterKey: 'hkpfsInformation.HKPFSReferenceNumber',
    },
    validHKPFS: {
      title: 'Valid HKPFS',
      type: 'singleSelect' as FilterType,
      options: booleanStringYNOptions,
      convertFilterFunction: (value: string | string[]) => {
        return {
          hkpfsInformation: {
            validHKPFSApplication: value === 'true',
          },
        };
      },
    },
    fundingScheme: {
      title: 'Nominated Funding',
      type: 'select' as FilterType,
      options: fundingSources,
      convertFilterFunction: (value: string | string[], appliedFilter: any) => {
        const validStatus = Boolean(appliedFilter.fundingSchemeStatus);
        if (validStatus) {
          const fundingStatusFilter: string[] =
            appliedFilter.fundingSchemeStatus ?? [];
          const arrayValue = value as string[];
          return {
            nomination: {
              fundings: {
                elemMatch: {
                  source: { in: arrayValue },
                  status: { in: fundingStatusFilter },
                },
              },
            },
          };
        } else {
          return {
            nomination: {
              fundings: {
                source: { in: value },
              },
            },
          };
        }
      },
    },
    fundingSchemeStatus: {
      title: 'Funding Status',
      type: 'select' as FilterType,
      options: getFundingStatusFilterOptions(
        FUNDING_STATUS,
        FUNDING_STATUS_DISPLAY_STRING,
      ),
      convertFilterFunction: (value: string | string[], appliedFilter: any) => {
        const validScheme = Boolean(appliedFilter.fundingScheme);
        if (validScheme) {
          // handled in fundingScheme's convertFilterFunction
          return undefined;
        } else {
          const fundingStatusFilter = value;
          return {
            nomination: {
              fundings: {
                status: { in: fundingStatusFilter },
              },
            },
          };
        }
      },
    },
    applicantSurname: {
      title: 'Applicant Surname',
      type: 'text' as FilterType,
      filterKey: 'applicant.name.surname',
    },
    applicantGivenName: {
      title: 'Applicant Given Name',
      type: 'text' as FilterType,
      filterKey: 'applicant.name.givenName',
    },
    applicationEmail: {
      title: 'Applicant Email',
      type: 'text' as FilterType,
      filterKey: 'applicant.email',
    },
    applicationNo: {
      title: 'Application No',
      type: 'text' as FilterType,
    },
    applicationStatus: {
      title: 'Application Status',
      type: 'select' as FilterType,
      options: currentStatus(onlyPaidApplicationStatusList),
      filterKey: 'status',
    },
    gsAssignee: {
      title: 'GS Assignee',
      type: 'select' as FilterType,
      options: gsAssigneeOption,
      filterKey: 'gsDecision.assignee',
      convertFilterFunction: (value: string | string[]) => {
        const valueArray = value as string[];

        const newValue = valueArray.map(valueItem =>
          valueItem === MONITOR_LIST_TEXT.UNASSIGNED ? null : valueItem,
        );
        return {
          gsDecision: {
            assignee: {
              in: newValue,
            },
          },
        };
      },
    },
  };

  return filters;
};

const convertFilterByFilterKey = (
  filterKey: string,
  value: string | string[],
): Record<string, any> => {
  const keyArray = filterKey.split('.');
  const reversedKeyArray = keyArray.reverse();
  const convertedFilter = reversedKeyArray.reduce((acc: any, key, index) => {
    if (index === 0) {
      acc = getFilterValue(value);
    }
    acc = { [key]: acc };
    return acc;
  }, {});
  return convertedFilter;
};
const getFilterValue = (value: boolean | string | string[]): any => {
  if (Array.isArray(value)) {
    return { in: value };
  } else if (typeof value === 'string') {
    return { regex: value };
  } else {
    return value;
  }
};
export const getTableQueryFilter = (
  appliedFilter: Record<string, string | string[]>,
  tableFilter: Record<string, Filter>,
): Record<string, any> | undefined => {
  const queryFilters = Object.entries(appliedFilter).reduce(
    (acc: any[], [id, value]) => {
      const filterKey = tableFilter[id]?.filterKey;
      const convertFilterFunction = tableFilter[id]?.convertFilterFunction;
      if (convertFilterFunction) {
        const convertedFilter = convertFilterFunction(value, appliedFilter);
        if (!convertedFilter) {
          return acc;
        }
        return [...acc, convertedFilter];
      }
      if (filterKey) {
        const convertedFilter = convertFilterByFilterKey(filterKey, value);
        return [...acc, convertedFilter];
      }
      // if no matched filterKey, use id as filter key
      return [...acc, { [id]: getFilterValue(value) }];
    },
    [],
  );
  if (queryFilters?.length > 1) {
    return { and: queryFilters };
  } else if (queryFilters?.length === 1) {
    return queryFilters[0];
  } else {
    return undefined;
  }
};

export const getQueryFilter = (
  tableFilter?: Record<string, any>,
  listFilter?: Record<string, any>,
  quickFilter?: Record<string, any>,
): Record<string, any> | undefined => {
  const filterArray = [];
  if (tableFilter) {
    filterArray.push(tableFilter);
  }
  if (listFilter) {
    filterArray.push(listFilter);
  }

  if (quickFilter) {
    filterArray.push(quickFilter);
  }

  switch (filterArray.length) {
    case 0:
      return undefined;
    case 1:
      return filterArray[0];
    default:
      return { and: filterArray };
  }
};

export const getDefaultQuickFilter = (
  quickFilters?: QuickFilter[],
): QuickFilter | undefined => {
  return quickFilters?.find(filter => filter.isDefault);
};
